import { useEffect, useMemo, useState } from 'react';
import type { UserSummary } from '@todo/shared';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../api/user.api';

/**
 * Choose who does the work, and optionally who leads.
 *
 * Loads the org's people once and filters in memory - a search-as-you-type
 * request per keystroke is wasted for a list this size, and it makes the
 * picker feel slower than scrolling it.
 */

interface AssigneePickerProps {
  /** Marks the field required and adjusts the helper text (task creation). */
  required?: boolean;
  /** When set, only that department's members are offered. */
  departmentId?: string;
  /**
   * Department must be chosen before anyone can be assigned (task
   * creation) - picking WHO belongs to the work first is what makes the
   * department filter above meaningful, rather than a no-op.
   */
  departmentRequired?: boolean;
  selected: string[];
  leadId: string | undefined;
  onChange: (ids: string[], leadId: string | undefined) => void;
  /** Shown when the completion policy demands a lead. */
  requireLead?: boolean;
  error?: string;
  disabled?: boolean;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('');
}

export function AssigneePicker({
  selected,
  leadId,
  onChange,
  requireLead,
  error,
  disabled,
  required = false,
  departmentId,
  departmentRequired = false,
}: AssigneePickerProps) {
  const [people, setPeople] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    userApi
      .list({ status: 'ACTIVE', pageSize: 100 })
      .then((res) => {
        if (!cancelled) setPeople(res.data);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const { user: me } = useAuth();

  /** Assignment hierarchy, mirror of the server rule: work flows DOWN or
   *  SIDEWAYS, never up. Peers at every level may pick each other - Admin
   *  to Admin, Super Admin to Super Admin, employee to employee - but
   *  nobody sees someone who outranks them. Self always shows. */
  const RANK: Record<string, number> = { SUPER_ADMIN: 3, ADMIN: 2 };
  const myRank = RANK[me?.role.systemKey ?? ''] ?? 1;
  const assignable = (p: UserSummary): boolean => {
    if (p.id === me?.id) return true;
    const rank = RANK[p.roleSystemKey ?? ''] ?? 1;
    return rank <= myRank;
  };

  const chosen = useMemo(
    () => selected.map((id) => people.find((p) => p.id === id)).filter(Boolean) as UserSummary[],
    [selected, people],
  );

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    return people
      .filter(assignable)
      // A chosen department narrows the offer to its MEMBERS - picking the
      // department first is how you say WHO this work belongs to, and that
      // applies to EVERY role: an admin appears under the department they
      // belong to (see the People page), which is exactly how Admin-to-
      // Admin and Super Admin-to-Super Admin assignment surfaces here. The
      // rank filter above still hides anyone who OUTRANKS the caller, so
      // an Admin never sees a Super Admin.
      .filter((p) => !departmentId || p.departmentId === departmentId)
      .filter((p) => !selected.includes(p.id))
      .filter((p) =>
        !term ||
        p.displayName.toLowerCase().includes(term) ||
        p.username.toLowerCase().includes(term) ||
        (p.employeeId ?? '').toLowerCase().includes(term));
  }, [people, selected, search, departmentId, me?.id]);

  // Department comes first: until one is picked, there is no "who this work
  // belongs to" for the assignee list to be scoped to, so searching/adding
  // is blocked and the helper text says why.
  const needsDepartment = departmentRequired && !departmentId;

  function add(id: string) {
    // First person added becomes the lead when one is required, so the common
    // case needs no extra click.
    const nextLead = requireLead && selected.length === 0 ? id : leadId;
    onChange([...selected, id], nextLead);
    setSearch('');
  }

  function remove(id: string) {
    const next = selected.filter((s) => s !== id);
    // Removing the lead must clear the flag, or the request fails validation
    // with "the lead must also be one of the assignees".
    onChange(next, leadId === id ? undefined : leadId);
  }

  return (
    <div>
      <span className="block text-sm font-medium text-slate-700">
        Assign to{' '}
        {required
          ? <span className="ml-0.5 text-red-500">*</span>
          : <span className="ml-1 text-slate-400">(optional)</span>}
      </span>

      {chosen.length > 0 && (
        <ul className="mt-1.5 space-y-1.5">
          {chosen.map((person) => (
            <li
              key={person.id}
              className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2"
            >
              {person.avatarUrl ? (
                <img src={person.avatarUrl} alt=""
                     className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-100 text-[10px] font-semibold text-brand-700">
                  {initials(person.displayName)}
                </span>
              )}

              <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
                {person.displayName}
                {person.employeeId && (
                  <span className="ml-1.5 font-mono text-xs text-slate-400">
                    {person.employeeId}
                  </span>
                )}
              </span>

              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(selected, leadId === person.id ? undefined : person.id)}
                title="A lead is the only person who can complete the task when the policy is Lead only"
                className={[
                  'rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide transition',
                  leadId === person.id
                    ? 'bg-amber-100 uppercase text-amber-700'
                    : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600',
                ].join(' ')}
              >
                {leadId === person.id ? '★ Lead' : 'Make lead'}
              </button>

              <button
                type="button"
                disabled={disabled}
                onClick={() => remove(person.id)}
                aria-label={`Remove ${person.displayName}`}
                className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative mt-1.5">
        <input
          type="text"
          value={search}
          disabled={disabled || loading || needsDepartment}
          placeholder={
            needsDepartment
              ? 'Choose a department first'
              : loading ? 'Loading people...' : 'Search by name or employee ID'
          }
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          // A click inside the list fires before blur would close it, so the
          // close is deferred by a tick.
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />

        {open && !needsDepartment && matches.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {matches.slice(0, 30).map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(person.id)}
                  className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition hover:bg-slate-50"
                >
                  {person.avatarUrl ? (
                    <img src={person.avatarUrl} alt=""
                         className="h-7 w-7 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-100 text-[10px] font-semibold text-brand-700">
                      {initials(person.displayName)}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-800">
                      {person.displayName}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {person.roleName}
                      {person.jobTitle ? ` · ${person.jobTitle}` : ''}
                      {person.employeeId ? ` · ${person.employeeId}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : needsDepartment ? (
        <p className="mt-1 text-xs text-amber-600">
          Select a department first, then choose who to assign.
        </p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          {required
            ? 'Pick at least one person - choosing yourself keeps the task on your own plate.'
            : 'The task appears on each assignee\'s board straight away.'}
        </p>
      )}
    </div>
  );
}
